import { type ParseEventScheduleResponse, type SeriesSummary } from "@crowdcoursing/api/openapi";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";

import { Dialog } from "../../atoms/Dialog/Dialog";
import {
  Date,
  FIELD_NAME_DATE,
  FIELD_NAME_TIME_RANGE,
  Form,
  type FormProps,
  ResetButton,
  SubmitButton,
  type TextFieldValue,
  TimeRange,
} from "../../atoms/Form";
import { CapacityCounter } from "../../molecules/CapacityCounter/CapacityCounter";
import { MeetingTimeSummary } from "../../molecules/MeetingTimeSummary/MeetingTimeSummary";
import {
  useMeetingTimeFieldBlur,
  useMeetingTimeSlots,
  useSlotExpansion,
  useTimeSlots,
} from "./AddEventTimesModal.hooks";
import {
  buildMeetingTimesFromRecurrence,
  createDefaultCustomRecurrence,
  type CustomRecurrenceConfig,
  EVENT_TIMES_CAP_MESSAGE,
  generateOccurrenceDatesWithCapInfo,
  getEffectiveRecurrenceConfig,
  getEventTimesCapMessage,
  getRecurrenceDisplayLabel,
  getRecurrenceOptionLabel,
  normalizeMeetingTimesForSubmit,
  type RecurrencePreset,
  type RecurrenceState,
} from "./AddEventTimesModal.recurrence";
import {
  hasApplicableSchedule,
  mapApiRecurrenceToCustomConfig,
  mapApiRecurrenceToState,
  mapExplicitMeetingTimes,
  parseDateOnly,
  parseTimeOnDate,
} from "./AddEventTimesModal.scheduleAi";
import { errorOutlinedButtonSx, successDialogTitleSx, successGradientButtonSx } from "./AddEventTimesModal.theme";
import { type AddEventTimesModalProps, type FormValues } from "./AddEventTimesModal.types";
import {
  BUILDER_FIELD_CAPACITY,
  BUILDER_FIELD_DATE,
  BUILDER_FIELD_SERIES_ID,
  clearMeetingTimeSlotFields,
  createResetFormValues,
  extractBuilderTimeRangesFromForm,
  extractMeetingTimesFromForm,
  generateBuilderTimeRangeFieldName,
  generateFieldName,
  isBuilderFormComplete,
  isMeetingTimeComplete,
  populateMeetingTimeSlotsInForm,
  STRINGS,
} from "./AddEventTimesModal.utils";
import { CustomRecurrenceDialog } from "./CustomRecurrenceDialog";
import { EventScheduleVoicePanel } from "./EventScheduleVoicePanel";
import { useEventScheduleAssistant } from "./useEventScheduleAssistant";

interface AddEventTimesDialogProps extends Omit<AddEventTimesModalProps, "disabled" | "eventName"> {
  onClose: () => void;
  open: boolean;
  series?: SeriesSummary[];
}

const RECURRENCE_PRESETS: RecurrencePreset[] = ["none", "daily", "weekly", "monthly", "annually", "weekdays", "custom"];

const createInitialRecurrence = (): RecurrenceState => ({
  preset: "none",
});

type BuilderInputMode = "classic" | "voice";

type WizardStep = "generate" | "review";

interface FormApi {
  change: (
    field: string,
    value: DateTime | [DateTime | null, DateTime | null] | TextFieldValue | number | null,
  ) => void;
  getState: () => { values: FormValues };
}

interface ApplyParsedScheduleOptions {
  defaultCapacity: number;
  validSeriesIds: ReadonlySet<string>;
}

const toValidSeriesIds = (series?: SeriesSummary[]): ReadonlySet<string> => {
  return new Set(series?.map((seriesItem) => seriesItem.id).filter((id): id is string => Boolean(id)) ?? []);
};

export const AddEventTimesDialog = ({
  defaultDate,
  eventId,
  onClose,
  onSubmit,
  open,
  series,
}: AddEventTimesDialogProps) => {
  const [recurrence, setRecurrence] = useState<RecurrenceState>(createInitialRecurrence);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [recurrenceBeforeCustom, setRecurrenceBeforeCustom] = useState<RecurrenceState>(createInitialRecurrence);
  const [customConfig, setCustomConfig] = useState<CustomRecurrenceConfig>(() =>
    createDefaultCustomRecurrence(DateTime.now()),
  );
  const [generationCapMessage, setGenerationCapMessage] = useState<string | null>(null);
  const [inputMode, setInputMode] = useState<BuilderInputMode>("classic");
  const [scheduleDescription, setScheduleDescription] = useState("");
  const [scheduleWarnings, setScheduleWarnings] = useState<string[] | null>(null);
  const [isApplyingSchedule, setIsApplyingSchedule] = useState(false);
  const [wizardStep, setWizardStep] = useState<WizardStep>("generate");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<FormApi>();
  const { error: scheduleError, isParsing, parseDescription } = useEventScheduleAssistant(eventId);

  const slotsHook = useMeetingTimeSlots();
  const { meetingTimeSlots, removeMeetingTimeSlot, replaceMeetingTimeSlots, resetSlots } = slotsHook;
  const builderTimeSlotsHook = useTimeSlots();
  const {
    addTimeSlot,
    removeTimeSlot,
    replaceTimeSlots,
    resetTimeSlots,
    timeSlots: builderTimeSlots,
  } = builderTimeSlotsHook;
  const expansionHook = useSlotExpansion();
  const { cleanupSlotExpansion, collapseSlot, expandedSlots, resetExpansion, toggleSlotExpansion } = expansionHook;
  const { clearTimeouts, handleFieldBlur, handleFieldFocus } = useMeetingTimeFieldBlur();

  useEffect(() => {
    if (open && defaultDate && formRef.current) {
      formRef.current.change(BUILDER_FIELD_DATE, defaultDate);
      setCustomConfig(createDefaultCustomRecurrence(defaultDate));
    }
  }, [defaultDate, open]);

  const closeDialog = useCallback(() => {
    onClose();
    resetSlots();
    resetTimeSlots();
    resetExpansion();
    clearTimeouts();
    setRecurrence(createInitialRecurrence());
    setCustomDialogOpen(false);
    setCustomConfig(createDefaultCustomRecurrence(defaultDate ?? DateTime.now()));
    setGenerationCapMessage(null);
    setInputMode("classic");
    setScheduleDescription("");
    setScheduleWarnings(null);
    setIsApplyingSchedule(false);
    setWizardStep("generate");
  }, [clearTimeouts, defaultDate, onClose, resetExpansion, resetSlots, resetTimeSlots]);

  const handleRecurrenceChange = useCallback(
    (preset: RecurrencePreset, startDate: DateTime | null) => {
      if (preset === "custom") {
        setRecurrenceBeforeCustom(recurrence);
        setCustomConfig(
          recurrence.custom ??
            (startDate ? createDefaultCustomRecurrence(startDate) : createDefaultCustomRecurrence(DateTime.now())),
        );
        setRecurrence((current) => ({ ...current, preset: "custom" }));
        setCustomDialogOpen(true);
        return;
      }

      setRecurrence({ preset });
    },
    [recurrence],
  );

  const handleCustomRecurrenceDone = useCallback((config: CustomRecurrenceConfig) => {
    setCustomConfig(config);
    setRecurrence({ custom: config, preset: "custom" });
    setCustomDialogOpen(false);
  }, []);

  const handleSubmit = useCallback<FormProps<FormValues>["onSubmit"]>(
    (formValues: FormValues) => {
      const meetingTimes = normalizeMeetingTimesForSubmit(extractMeetingTimesFromForm(formValues, meetingTimeSlots), {
        validSeriesIds: toValidSeriesIds(series),
      });

      if (meetingTimes.length > 0) {
        onSubmit(meetingTimes, eventId);
        closeDialog();
      }
    },
    [closeDialog, eventId, meetingTimeSlots, onSubmit, series],
  );

  const handleGenerate = useCallback(
    (
      formValues: FormValues,
      formApi: FormApi,
      recurrenceOverride?: RecurrenceState,
      builderTimeSlotIdsOverride?: string[],
    ) => {
      const activeRecurrence = recurrenceOverride ?? recurrence;
      const builderTimeSlotIds = builderTimeSlotIdsOverride ?? builderTimeSlots.map((slot) => slot.id);
      const date = formValues[BUILDER_FIELD_DATE] as DateTime | null;
      const capacity = formValues[BUILDER_FIELD_CAPACITY];
      const seriesId = formValues[BUILDER_FIELD_SERIES_ID] as string | null;

      if (
        !date ||
        capacity === undefined ||
        capacity === null ||
        !isBuilderFormComplete(formValues, builderTimeSlotIds)
      ) {
        return;
      }

      clearMeetingTimeSlotFields(meetingTimeSlots, formApi);

      const recurrenceConfig = getEffectiveRecurrenceConfig(activeRecurrence, date);
      const timeRanges = extractBuilderTimeRangesFromForm(formValues, builderTimeSlotIds);
      const occurrenceGeneration = generateOccurrenceDatesWithCapInfo(date, recurrenceConfig, {
        timeRangeCount: timeRanges.length,
      });
      const generatedMeetingTimes = buildMeetingTimesFromRecurrence({
        capacity: Number(capacity),
        occurrenceDates: occurrenceGeneration.dates,
        seriesId: seriesId || undefined,
        timeRanges,
      });

      if (generatedMeetingTimes.length === 0) {
        return;
      }

      setGenerationCapMessage(getEventTimesCapMessage(occurrenceGeneration));

      const newSlots = replaceMeetingTimeSlots(generatedMeetingTimes.length);
      resetExpansion();

      setTimeout(() => {
        populateMeetingTimeSlotsInForm(generatedMeetingTimes, newSlots, formApi);
        setWizardStep("review");
      }, 0);
    },
    [builderTimeSlots, meetingTimeSlots, recurrence, replaceMeetingTimeSlots, resetExpansion],
  );

  const applyParsedSchedule = useCallback(
    (response: ParseEventScheduleResponse, formApi: FormApi, options: ApplyParsedScheduleOptions) => {
      setScheduleWarnings(response.warnings ?? null);
      const { defaultCapacity, validSeriesIds } = options;

      if (response.mode === "explicit" && response.meetingTimes?.length) {
        const requestedCount = response.meetingTimes.length;
        const meetingTimes = mapExplicitMeetingTimes(response.meetingTimes, {
          defaultCapacity,
          validSeriesIds,
        });

        if (meetingTimes.length === 0) {
          return;
        }

        clearMeetingTimeSlotFields(meetingTimeSlots, formApi);
        const newSlots = replaceMeetingTimeSlots(meetingTimes.length);
        resetExpansion();
        setGenerationCapMessage(meetingTimes.length < requestedCount ? EVENT_TIMES_CAP_MESSAGE : null);

        setTimeout(() => {
          populateMeetingTimeSlotsInForm(meetingTimes, newSlots, formApi);
          setWizardStep("review");
        }, 0);

        return;
      }

      const builder = response.builder;
      const startDate = builder?.startDate;
      const builderTimeRanges = builder?.timeRanges;
      if (!startDate || !builderTimeRanges?.length) {
        return;
      }

      const nextRecurrence = mapApiRecurrenceToState(builder.recurrence, parseDateOnly(startDate));
      const nextCustomConfig = mapApiRecurrenceToCustomConfig(builder.recurrence);
      const timeRangeCount = builderTimeRanges.length;
      const builderTimeSlotIds = Array.from({ length: timeRangeCount }, (_, index) => String(index));

      setRecurrence(nextRecurrence);
      if (nextCustomConfig) {
        setCustomConfig(nextCustomConfig);
      }

      replaceTimeSlots(timeRangeCount);

      setTimeout(() => {
        formApi.change(BUILDER_FIELD_DATE, parseDateOnly(startDate));
        formApi.change(
          BUILDER_FIELD_CAPACITY,
          String(builder.capacity != null && builder.capacity > 0 ? builder.capacity : defaultCapacity),
        );

        const seriesId =
          builder.meetingTimeSeriesId && validSeriesIds.has(builder.meetingTimeSeriesId)
            ? builder.meetingTimeSeriesId
            : null;
        formApi.change(BUILDER_FIELD_SERIES_ID, seriesId);

        builderTimeRanges.forEach((range, index) => {
          if (!range.start || !range.end) {
            return;
          }

          formApi.change(generateBuilderTimeRangeFieldName(String(index)), [
            parseTimeOnDate(startDate, range.start),
            parseTimeOnDate(startDate, range.end),
          ]);
        });

        setTimeout(() => {
          handleGenerate(formApi.getState().values, formApi, nextRecurrence, builderTimeSlotIds);
        }, 0);
      }, 0);
    },
    [handleGenerate, meetingTimeSlots, replaceMeetingTimeSlots, replaceTimeSlots, resetExpansion],
  );

  const handleApplyDescription = useCallback(
    async (description: string, defaultCapacity: number) => {
      setIsApplyingSchedule(true);
      setScheduleWarnings(null);

      try {
        const response = await parseDescription(description, {
          defaultCapacity,
          series: series?.map((seriesItem) => ({ id: seriesItem.id, name: seriesItem.name })),
        });

        if (!response) {
          return;
        }

        if (!hasApplicableSchedule(response)) {
          setScheduleWarnings(
            response.warnings?.length ? response.warnings : ["No event times could be parsed from that description."],
          );
          return;
        }

        if (formRef.current) {
          applyParsedSchedule(response, formRef.current, {
            defaultCapacity,
            validSeriesIds: toValidSeriesIds(series),
          });
        }
      } finally {
        setIsApplyingSchedule(false);
      }
    },
    [applyParsedSchedule, parseDescription, series],
  );

  const handleRemoveMeetingTime = useCallback(
    (slotId: string) => {
      removeMeetingTimeSlot(slotId);
      cleanupSlotExpansion(slotId);
    },
    [cleanupSlotExpansion, removeMeetingTimeSlot],
  );

  return (
    <Form<FormValues> onSubmit={handleSubmit}>
      {({ form, values }) => {
        formRef.current = form;
        const startDate = (values[BUILDER_FIELD_DATE] as DateTime | null) ?? null;
        const builderTimeSlotIds = builderTimeSlots.map((slot) => slot.id);
        const builderTimeRanges = extractBuilderTimeRangesFromForm(values, builderTimeSlotIds);
        const recurrenceConfig = startDate ? getEffectiveRecurrenceConfig(recurrence, startDate) : null;
        const previewGeneration =
          startDate && recurrenceConfig && builderTimeRanges.length > 0
            ? generateOccurrenceDatesWithCapInfo(startDate, recurrenceConfig, {
                timeRangeCount: builderTimeRanges.length,
              })
            : null;
        const previewCount = previewGeneration?.eventTimeCount ?? 0;
        const previewCapMessage = previewGeneration ? getEventTimesCapMessage(previewGeneration) : null;
        const recurrenceLabel = getRecurrenceDisplayLabel(recurrence, startDate);
        const canGenerate = isBuilderFormComplete(values, builderTimeSlotIds);
        const canSubmit =
          meetingTimeSlots.length > 0 && meetingTimeSlots.every((slot) => isMeetingTimeComplete(slot.id, values));
        const canContinueToReview = meetingTimeSlots.length > 0;
        const activeWizardStep = wizardStep === "generate" ? 0 : 1;

        const resetWizard = () => {
          resetSlots();
          resetTimeSlots();
          resetExpansion();
          clearTimeouts();
          setRecurrence(createInitialRecurrence());
          setCustomConfig(createDefaultCustomRecurrence(defaultDate ?? DateTime.now()));
          setGenerationCapMessage(null);
          setInputMode("classic");
          setScheduleDescription("");
          setScheduleWarnings(null);
          setWizardStep("generate");
        };

        return (
          <>
            <Dialog
              contentSx={{
                display: "flex",
                flex: 1,
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
                p: 0,
              }}
              disableEnforceFocus
              actions={
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0, px: 2, py: 1.5 }}>
                    <Stepper activeStep={activeWizardStep} alternativeLabel>
                      <Step>
                        <StepLabel>{STRINGS.tWizardStepGenerate}</StepLabel>
                      </Step>
                      <Step>
                        <StepLabel>{STRINGS.tWizardStepReview}</StepLabel>
                      </Step>
                    </Stepper>
                  </Box>

                  <Box ref={scrollContainerRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
                    {wizardStep === "generate" ? (
                      <Stack spacing={2}>
                        {inputMode === "classic" ? (
                          <Paper
                            sx={{
                              alignItems: "center",
                              bgcolor: "action.hover",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 2,
                              justifyContent: "space-between",
                              p: 1.5,
                            }}
                            variant="outlined"
                          >
                            <Typography variant="body2">{STRINGS.tVoiceDescriptionHint}</Typography>
                            <Button
                              onClick={() => setInputMode("voice")}
                              startIcon={<RecordVoiceOverIcon />}
                              sx={successGradientButtonSx}
                              variant="contained"
                            >
                              {STRINGS.tTryVoiceNow}
                            </Button>
                          </Paper>
                        ) : (
                          <Stack spacing={1.5}>
                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                              <Button onClick={() => setInputMode("classic")} size="small" variant="text">
                                {STRINGS.tUseManualBuilder}
                              </Button>
                            </Box>
                            <EventScheduleVoicePanel
                              description={scheduleDescription}
                              eventId={eventId}
                              isApplying={isApplyingSchedule || isParsing}
                              onApplyDescription={(description) =>
                                handleApplyDescription(description, Number(values[BUILDER_FIELD_CAPACITY] ?? 0))
                              }
                              onDescriptionChange={setScheduleDescription}
                              warnings={scheduleWarnings}
                            />
                            {scheduleError ? (
                              <Typography color="error" variant="body2">
                                {scheduleError}
                              </Typography>
                            ) : null}
                          </Stack>
                        )}

                        {inputMode === "classic" ? (
                          <>
                            <Stack direction={{ sm: "row", xs: "column" }} spacing={2}>
                              <Box sx={{ flex: 1 }}>
                                <Date
                                  datePickerProps={{ disablePast: true }}
                                  label={STRINGS.tDate}
                                  name={BUILDER_FIELD_DATE}
                                  required
                                />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <CapacityCounter label={STRINGS.tCapacity} name={BUILDER_FIELD_CAPACITY} required />
                              </Box>
                            </Stack>

                            {builderTimeSlots.map((slot, index) => (
                              <Box key={slot.id} sx={{ alignItems: "flex-start", display: "flex", gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <TimeRange
                                    label={
                                      builderTimeSlots.length > 1
                                        ? `${STRINGS.tTimeRange} ${index + 1}`
                                        : STRINGS.tTimeRange
                                    }
                                    name={generateBuilderTimeRangeFieldName(slot.id)}
                                    required
                                  />
                                </Box>
                                {builderTimeSlots.length > 1 ? (
                                  <IconButton
                                    aria-label={STRINGS.tRemoveTime}
                                    color="error"
                                    onClick={() => {
                                      removeTimeSlot(slot.id);
                                      form.change(generateBuilderTimeRangeFieldName(slot.id), null);
                                    }}
                                    sx={{ mt: 3 }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                ) : null}
                              </Box>
                            ))}

                            <Button
                              onClick={() => addTimeSlot()}
                              startIcon={<AddIcon />}
                              sx={{ alignSelf: "flex-start" }}
                              variant="text"
                            >
                              {STRINGS.tAddAnotherTime}
                            </Button>

                            {series && series.length > 0 ? (
                              <FormControl fullWidth>
                                <InputLabel id="builder-series-select-label">{STRINGS.tSeries}</InputLabel>
                                <Select
                                  id="builder-series-select"
                                  label={STRINGS.tSeries}
                                  labelId="builder-series-select-label"
                                  onChange={(event) => form.change(BUILDER_FIELD_SERIES_ID, event.target.value)}
                                  value={(values[BUILDER_FIELD_SERIES_ID] as string) || ""}
                                >
                                  <MenuItem value="">
                                    <em>None</em>
                                  </MenuItem>
                                  {series.map((seriesItem) => (
                                    <MenuItem key={seriesItem.id} value={seriesItem.id}>
                                      {seriesItem.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : null}

                            <FormControl fullWidth>
                              <InputLabel id="recurrence-select-label">{STRINGS.tRecurrence}</InputLabel>
                              <Select
                                label={STRINGS.tRecurrence}
                                labelId="recurrence-select-label"
                                onChange={(event) =>
                                  handleRecurrenceChange(event.target.value as RecurrencePreset, startDate)
                                }
                                renderValue={() => recurrenceLabel}
                                value={recurrence.preset}
                              >
                                {RECURRENCE_PRESETS.map((preset) => (
                                  <MenuItem key={preset} value={preset}>
                                    {getRecurrenceOptionLabel(preset, startDate)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <Stack
                              alignItems={{ sm: "center", xs: "stretch" }}
                              direction={{ sm: "row", xs: "column" }}
                              spacing={2}
                            >
                              {previewCount > 0 ? (
                                <Box sx={{ flex: 1 }}>
                                  <Typography color="text.secondary" variant="body2">
                                    Will generate {previewCount} event {previewCount === 1 ? "time" : "times"} to review
                                    on the next step.
                                  </Typography>
                                  {previewCapMessage ? (
                                    <Typography color="warning.main" sx={{ mt: 0.5 }} variant="body2">
                                      {previewCapMessage}
                                    </Typography>
                                  ) : null}
                                </Box>
                              ) : (
                                <Box sx={{ flex: 1 }} />
                              )}
                              <Button
                                disabled={!canGenerate}
                                onClick={() => handleGenerate(values, form)}
                                sx={successGradientButtonSx}
                                variant="contained"
                              >
                                {STRINGS.tGenerate}
                              </Button>
                            </Stack>
                          </>
                        ) : null}
                      </Stack>
                    ) : (
                      <Stack spacing={2}>
                        <Typography fontWeight="bold" variant="subtitle1">
                          {STRINGS.tReviewHeading}
                        </Typography>

                        {generationCapMessage ? (
                          <Typography color="warning.main" variant="body2">
                            {generationCapMessage}
                          </Typography>
                        ) : null}

                        {meetingTimeSlots.length === 0 ? (
                          <Typography color="text.secondary" sx={{ fontStyle: "italic" }} variant="body2">
                            No event times to review. Go back to generate a schedule.
                          </Typography>
                        ) : null}

                        {meetingTimeSlots.map((slot) => (
                          <Paper
                            id={`meeting-time-${slot.id}`}
                            key={slot.id}
                            sx={{ border: "1px solid", borderColor: "divider", p: 2 }}
                          >
                            <Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", mb: 2 }}>
                              <Typography fontWeight="bold" variant="subtitle2">
                                {STRINGS.tMeetingTime} {slot.displayIndex}
                              </Typography>
                              <IconButton
                                aria-label={STRINGS.tRemoveTime}
                                color="error"
                                onClick={() => handleRemoveMeetingTime(slot.id)}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            <MeetingTimeSummary
                              capacityFieldName={generateFieldName(slot.id, "capacity")}
                              dateFieldName={generateFieldName(slot.id, FIELD_NAME_DATE)}
                              formValues={values}
                              isExpanded={expandedSlots.has(slot.id)}
                              onFieldBlur={(slotId) => handleFieldBlur(slotId, values, collapseSlot, expandedSlots)}
                              onFieldFocus={handleFieldFocus}
                              onToggleExpansion={toggleSlotExpansion}
                              slotId={slot.id}
                              timeRangeFieldName={generateFieldName(slot.id, FIELD_NAME_TIME_RANGE)}
                            />

                            <Collapse in={expandedSlots.has(slot.id)} timeout="auto">
                              <Stack spacing={2} sx={{ mt: 1 }}>
                                <Stack direction={{ sm: "row", xs: "column" }} spacing={2}>
                                  <Box sx={{ flex: 1 }}>
                                    <Date
                                      datePickerProps={{ disablePast: true }}
                                      label={STRINGS.tDate}
                                      name={generateFieldName(slot.id, FIELD_NAME_DATE)}
                                      required
                                    />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <CapacityCounter
                                      label={STRINGS.tCapacity}
                                      name={generateFieldName(slot.id, "capacity")}
                                      required
                                    />
                                  </Box>
                                </Stack>
                                <TimeRange
                                  label={STRINGS.tTimeRange}
                                  name={generateFieldName(slot.id, FIELD_NAME_TIME_RANGE)}
                                  required
                                />
                                {series && series.length > 0 ? (
                                  <FormControl fullWidth>
                                    <InputLabel id={`series-select-${slot.id}-label`}>{STRINGS.tSeries}</InputLabel>
                                    <Select
                                      id={`series-select-${slot.id}`}
                                      label={STRINGS.tSeries}
                                      labelId={`series-select-${slot.id}-label`}
                                      onChange={(event) =>
                                        form.change(generateFieldName(slot.id, "seriesId"), event.target.value)
                                      }
                                      value={(values[generateFieldName(slot.id, "seriesId")] as string) || ""}
                                    >
                                      <MenuItem value="">
                                        <em>None</em>
                                      </MenuItem>
                                      {series.map((seriesItem) => (
                                        <MenuItem key={seriesItem.id} value={seriesItem.id}>
                                          {seriesItem.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : null}
                              </Stack>
                            </Collapse>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Box
                    sx={{
                      alignItems: "center",
                      bgcolor: "background.paper",
                      borderColor: "divider",
                      borderTop: 1,
                      display: "flex",
                      flexShrink: 0,
                      flexWrap: "wrap",
                      gap: 2,
                      justifyContent: "space-between",
                      p: 2,
                    }}
                  >
                    <Button onClick={closeDialog} sx={errorOutlinedButtonSx} variant="outlined">
                      {STRINGS.tCancel}
                    </Button>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {wizardStep === "review" ? (
                        <Button onClick={() => setWizardStep("generate")} variant="outlined">
                          {STRINGS.tWizardBackToGenerate}
                        </Button>
                      ) : null}
                      <ResetButton onClick={resetWizard} resetValues={createResetFormValues(defaultDate, ["0"])}>
                        {STRINGS.tReset}
                      </ResetButton>
                      {wizardStep === "generate" && canContinueToReview ? (
                        <Button onClick={() => setWizardStep("review")} variant="outlined">
                          {STRINGS.tWizardContinueToReview}
                        </Button>
                      ) : null}
                      {wizardStep === "review" ? (
                        <SubmitButton disabled={!canSubmit} sx={successGradientButtonSx}>
                          {STRINGS.tCreate}
                        </SubmitButton>
                      ) : null}
                    </Box>
                  </Box>
                </Box>
              }
              dialogType="custom"
              maxWidth="md"
              onDialogClose={closeDialog}
              open={open}
              paperSx={{
                display: "flex",
                flexDirection: "column",
                maxHeight: "calc(100dvh - 32px)",
              }}
              title={
                <Box component="span" sx={{ alignItems: "center", display: "inline-flex", gap: 1 }}>
                  <ScheduleIcon fontSize="small" />
                  Create Event Times
                </Box>
              }
              titleSx={successDialogTitleSx}
            />

            <CustomRecurrenceDialog
              initialConfig={customConfig}
              onCancel={() => {
                setCustomDialogOpen(false);
                setRecurrence(recurrenceBeforeCustom);
              }}
              onDone={handleCustomRecurrenceDone}
              open={customDialogOpen}
              startDate={startDate}
            />
          </>
        );
      }}
    </Form>
  );
};
